import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ReactElement, ReactNode } from 'react';
import { Status } from '@pillar-portal/database/dist/frontend';
import {
    CredentialPendingReasonType,
    CredentialTypeDisplayName,
    CredentialType,
    ThirdPartySystemId,
    formatThirdPartySystemName,
    type CredentialPendingReason,
} from '@pillar-portal/api-types';

type CredentialCardDisplayData = {
    credential_uuid?: string;
    account_id: string;
    system_id: ThirdPartySystemId;
    credential_type: CredentialType;
    status: Status;
    pending_reason?: CredentialPendingReason | null;
    is_decryptable?: boolean;
    applies_to_children: boolean;
    public_fields: Record<string, unknown>;
};

type CredentialCardViewProps = {
    credential: CredentialCardDisplayData;
    showPublicFields: boolean;
    actions?: ReactNode;
    children?: ReactNode;
    id?: string;
    className?: string;
    titleHref?: string;
};

type PublicFieldEntry = {
    key: string;
    value: string;
};

function getSystemDisplayName(systemId: ThirdPartySystemId): string {
    try {
        return formatThirdPartySystemName(systemId);
    } catch {
        return `System ${systemId}`;
    }
}

function getCredentialPublicFieldEntries(
    credential: CredentialCardDisplayData
): ReadonlyArray<PublicFieldEntry> {
    return Object.keys(credential.public_fields)
        .sort()
        .flatMap((fieldName): PublicFieldEntry[] => {
            const rawFieldValue: unknown = credential.public_fields[fieldName];
            if (rawFieldValue !== null && rawFieldValue !== undefined) {
                return [{ key: fieldName, value: String(rawFieldValue) }];
            }
            return [];
        });
}

function getPendingReasonTooltip(
    pendingReason: CredentialPendingReason | null | undefined
): string {
    if (pendingReason?.reason_type === CredentialPendingReasonType.INCOMPLETE_CREDENTIAL) {
        return `The credential record is incomplete: it lacks ${pendingReason.missing_required_field_labels.join(', ')}.`;
    }
    return 'The credential is pending, but no more specific reason is available.';
}

export function CredentialStatusBadge({
    status,
    pendingReason,
}: {
    status: Status;
    pendingReason?: CredentialPendingReason | null;
}): ReactElement | null {
    if (status === Status.ACTIVE) {
        return null;
    }
    if (status === Status.PENDING) {
        return (
            <Badge
                className="border-yellow-300 bg-yellow-100 text-yellow-900 hover:bg-yellow-100"
                title={getPendingReasonTooltip(pendingReason)}
                tabIndex={0}
            >
                (PENDING)
            </Badge>
        );
    }
    return <Badge variant="destructive">({status})</Badge>;
}

export function CredentialCardView({
    credential,
    showPublicFields,
    actions,
    children,
    id,
    className,
    titleHref,
}: CredentialCardViewProps): ReactElement {
    return (
        <div
            id={id}
            data-credential-uuid={credential.credential_uuid}
            data-credential-account-id={credential.account_id}
            data-credential-status={credential.status}
            data-credential-system-id={credential.system_id}
            data-credential-type={credential.credential_type}
            className={`border rounded-lg p-4 transition-colors hover:bg-gray-50 ${className ?? ''}`.trim()}
        >
            <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="font-medium">
                            {titleHref === undefined ? (
                                credential.account_id
                            ) : (
                                <a className="text-primary underline" href={titleHref}>
                                    {credential.account_id}
                                </a>
                            )}
                        </h4>
                        <Badge variant="outline">
                            {getSystemDisplayName(credential.system_id)}
                        </Badge>
                        <Badge variant="secondary">
                            {CredentialTypeDisplayName[credential.credential_type]}
                        </Badge>
                        <CredentialStatusBadge
                            status={credential.status}
                            pendingReason={credential.pending_reason}
                        />
                        {credential.applies_to_children && (
                            <Badge variant="secondary">Inherited by Children</Badge>
                        )}
                    </div>
                    {showPublicFields && (
                        <div className="text-sm text-muted-foreground space-y-1">
                            {getCredentialPublicFieldEntries(credential).map((entry) => (
                                <div key={entry.key}>
                                    <span className="font-medium">{entry.key}</span>: {entry.value}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {actions}
            </div>
            {credential.is_decryptable === false && (
                <Alert variant="destructive" className="mt-3">
                    <AlertDescription>
                        Stored credential cannot be decrypted. Replace its credential fields.
                    </AlertDescription>
                </Alert>
            )}
            {children}
        </div>
    );
}
